import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { Box, cssVar, HStack, IconButton } from '@cad-arc';
import { htmlToText } from 'html-to-text';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import React, { memo, useCallback, useRef, useState } from 'react';

import {
    CommandListenersPlugin,
    ContentEditable,
    FlagNodes,
    FlagPlugin,
    $generateHtmlFromNodes,
    HistoryPlugin,
    LexicalComposer,
    LexicalErrorBoundary,
    OnChangePlugin,
    PlainTextPlugin,
} from '../../../common/components/lexical';
import { AdditionalInfoView } from '../../types';
import { MultiLineDetectorPlugin } from './multi-line-detector-plugin';
import { TabCyclePlugin } from './tab-cycle-plugin';

export interface NarrativeEditRowProps {
    info: AdditionalInfoView;
    agencyEventId: number;
    isFirstResponder?: boolean;
    onSave: (updatedInfo: AdditionalInfoView) => void;
    onCancel: () => void;
    isSaving?: boolean;
}

export const NarrativeEditRow = memo(function NarrativeEditRow({
    info,
    isFirstResponder = false,
    onSave,
    onCancel,
    isSaving = false,
}: NarrativeEditRowProps) {
    const textRef = useRef(info.text);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const saveButtonRef = useRef<HTMLButtonElement>(null);
    const [isMultiLine, setIsMultiLine] = useState(() => info.text.includes('\n'));
    const [hasChanged, setHasChanged] = useState(false);

    const handleChange = useCallback(
        (editorState, editor) => {
            editorState.read(() => {
                const html = $generateHtmlFromNodes(editor, null);
                textRef.current = htmlToText(html, {
                    preserveNewlines: true,
                    whitespaceCharacters: '',
                });
                setHasChanged(textRef.current !== info.text);
            });
        },
        [info.text]
    );

    const handleSave = useCallback(() => {
        if (!hasChanged) return;
        onSave({ ...info, text: textRef.current });
    }, [hasChanged, info, onSave]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onCancel();
            }
        },
        [onCancel]
    );

    const buttonSize = isFirstResponder ? 'lg' : 'sm';
    const fontSize = isFirstResponder ? '18px' : '14px';

    return (
        <Box w="100%" padding="var(--arc-space-1)" position="relative" onKeyDown={handleKeyDown}>
            <LexicalComposer
                initialConfig={{
                    namespace: 'narrative-edit',
                    nodes: [...FlagNodes],
                    onError: () => {},
                    editorState: () => {
                        const root = $getRoot();
                        const paragraph = $createParagraphNode();
                        paragraph.append($createTextNode(info.text));
                        root.append(paragraph);
                    },
                }}
            >
                <PlainTextPlugin
                    contentEditable={
                        <ContentEditable
                            style={{
                                backgroundColor: 'var(--arc-colors-brand-content)',
                                color: 'var(--arc-colors-text-primary)',
                                fontSize,
                                width: '100%',
                                outline: 'none',
                                padding:
                                    'var(--arc-space-2) 65px var(--arc-space-2) var(--arc-space-2)',
                                borderRadius: 'var(--arc-radii-md)',
                                border: '1px solid var(--arc-colors-border-default)',
                                maxHeight: 'var(--arc-space-60)',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                            }}
                            data-testid="NARRATIVE_EDIT_TEXTAREA"
                        />
                    }
                    placeholder={null}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <AutoFocusPlugin />
                <OnChangePlugin onChange={handleChange} />
                <CommandListenersPlugin onEnterDown={handleSave} />
                <MultiLineDetectorPlugin fontSize={fontSize} onMultiLineChange={setIsMultiLine} />
                <TabCyclePlugin cancelButtonRef={cancelButtonRef} saveButtonRef={saveButtonRef} />
                <FlagPlugin />
                <HistoryPlugin />
            </LexicalComposer>
            <HStack
                position="absolute"
                {...(isMultiLine
                    ? { bottom: 'var(--arc-space-4)' }
                    : { top: '50%', transform: 'translateY(-50%)' })}
                right="var(--arc-space-4)"
                zIndex={cssVar('arc.zIndices.dropdown')}
                spacing="var(--arc-space-1)"
            >
                <IconButton
                    ref={cancelButtonRef}
                    variant="outline"
                    size={buttonSize}
                    icon="CloseX"
                    onClick={onCancel}
                    isDisabled={isSaving}
                    data-testid="NARRATIVE_EDIT_CANCEL"
                    aria-label="Cancel edit"
                />
                <IconButton
                    ref={saveButtonRef}
                    variant="solid"
                    size={buttonSize}
                    icon="Check"
                    onClick={handleSave}
                    isLoading={isSaving}
                    isDisabled={!hasChanged}
                    data-testid="NARRATIVE_EDIT_SAVE"
                    aria-label="Save edit"
                />
            </HStack>
        </Box>
    );
});
